import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  loadWizardState,
  initWizard,
  updateWizardStep,
  setCurrentStep,
  completeWizard,
  resetWizard,
} from '../wizardSlice';

export const useWizard = (shopId, wizardType = 'integration-wizard-v2', wizardConfig = null) => {
  const dispatch = useDispatch();
  const wizardState = useSelector((s) => s.integrationWizard?.wizards?.[wizardType]);
  const { state, loading, saving, error } = wizardState || {};

  const [localStepIndex, setLocalStepIndex] = useState(0);

  useEffect(() => {
    if (shopId && wizardType) {
      dispatch(loadWizardState(shopId, wizardType));
    }
  }, [shopId, wizardType, dispatch]);

  useEffect(() => {
    if (state?.currentStep !== undefined) {
      setLocalStepIndex(state.currentStep);
    }
  }, [state?.currentStep]);

  const wizardData = state?.wizardData || {};

  const hasConfig = wizardConfig && typeof wizardConfig.getActiveSteps === 'function';

  const activeSteps = hasConfig ? wizardConfig.getActiveSteps(wizardData) : [];
  const currentStepIndex = hasConfig
    ? Math.min(localStepIndex, activeSteps.length - 1)
    : localStepIndex;
  const currentStep = hasConfig ? activeSteps[currentStepIndex] : null;

  const canGoNext = hasConfig ? wizardConfig.canProceed(currentStepIndex, wizardData) : true;
  const canGoPrev = currentStepIndex > 0;
  const isLastStep = hasConfig ? (currentStepIndex === activeSteps.length - 1) : false;

  const goNext = useCallback(() => {
    if (!canGoNext || isLastStep) return;

    const nextIndex = currentStepIndex + 1;
    setLocalStepIndex(nextIndex);
    dispatch(setCurrentStep(shopId, wizardType, nextIndex));
  }, [canGoNext, isLastStep, currentStepIndex, shopId, wizardType, dispatch]);

  const goPrev = useCallback(() => {
    if (!canGoPrev) return;

    const prevIndex = currentStepIndex - 1;
    setLocalStepIndex(prevIndex);
    dispatch(setCurrentStep(shopId, wizardType, prevIndex));
  }, [canGoPrev, currentStepIndex, shopId, wizardType, dispatch]);

  const goToStep = useCallback(
    (stepIndex) => {
      // If we have active steps defined, validate bounds against them
      if (activeSteps.length > 0) {
        if (stepIndex < 0 || stepIndex >= activeSteps.length) return;
      } else if (stepIndex < 0) {
        // Otherwise just prevent negative index
        return;
      }

      setLocalStepIndex(stepIndex);
      dispatch(setCurrentStep(shopId, wizardType, stepIndex));
    },
    [activeSteps.length, shopId, wizardType, dispatch],
  );

  const updateStep = useCallback(
    (stepData) => {
      if (!currentStep) return;

      dispatch(
        updateWizardStep(shopId, wizardType, currentStep.id, currentStepIndex, stepData),
      );
    },
    [currentStep, currentStepIndex, shopId, wizardType, dispatch],
  );

  const complete = useCallback(() => {
    dispatch(completeWizard(shopId, wizardType));
  }, [shopId, wizardType, dispatch]);

  const reset = useCallback(() => {
    dispatch(resetWizard(shopId, wizardType));
  }, [shopId, wizardType, dispatch]);

  const init = useCallback(
    (initialData = {}) => {
      dispatch(initWizard(shopId, wizardType, initialData));
    },
    [shopId, wizardType, dispatch],
  );

  return {
    state,
    data: wizardData,
    currentStepIndex,
    currentStep,
    activeSteps,
    canGoNext,
    canGoPrev,
    isLastStep,
    goNext,
    goPrev,
    goToStep,
    updateStep,
    complete,
    reset,
    init,
    loading,
    saving,
    error,
  };
};

export default useWizard;
